(function () {
  'use strict';

  if (window.__AG_COUNTDOWN_BANNER_LOADED__) {
    return;
  }

  window.__AG_COUNTDOWN_BANNER_LOADED__ = true;

  const DEFAULT_PERSISTED_STORAGE_KEY = 'ag_homepage_migration_timer_v2';
  const DEFAULT_DURATION_MS = 48 * 60 * 60 * 1000;
  const persistedEndCache = new Map();
  const memoryStorage = new Map();
  const hhmmssTimers = [];
  const mmssmsTimers = [];
  let storageAvailable = null;
  let hhmmssInterval = null;
  let mmssmsInterval = null;

  function canUseStorage() {
    if (storageAvailable !== null) {
      return storageAvailable;
    }

    try {
      const testKey = '__ag_countdown_storage_test__';
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
      storageAvailable = true;
    } catch (error) {
      storageAvailable = false;
    }

    return storageAvailable;
  }

  function readStorage(key) {
    if (canUseStorage()) {
      try {
        const value = window.localStorage.getItem(key);
        if (value !== null) {
          memoryStorage.set(key, value);
          return value;
        }
      } catch (error) {
        /* fall through to memory */
      }
    }

    return memoryStorage.has(key) ? memoryStorage.get(key) : null;
  }

  function writeStorage(key, value) {
    memoryStorage.set(key, String(value));

    if (!canUseStorage()) {
      return;
    }

    try {
      window.localStorage.setItem(key, String(value));
    } catch (error) {
      /* memory fallback only */
    }
  }

  function removeStorage(key) {
    memoryStorage.delete(key);
    persistedEndCache.delete(key);

    if (!canUseStorage()) {
      return;
    }

    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      /* ignore */
    }
  }

  function getPersistedStorageKey(bannerElement) {
    return bannerElement.getAttribute('data-countdown-storage-key') || DEFAULT_PERSISTED_STORAGE_KEY;
  }

  function getCountdownDurationMs(bannerElement) {
    const hours = parseInt(bannerElement.getAttribute('data-countdown-hours'), 10);
    if (!Number.isNaN(hours) && hours > 0) {
      return hours * 60 * 60 * 1000;
    }

    const minutes = parseInt(bannerElement.getAttribute('data-countdown-minutes'), 10);
    if (!Number.isNaN(minutes) && minutes > 0) {
      return minutes * 60 * 1000;
    }

    return DEFAULT_DURATION_MS;
  }

  function resolvePersistedEndTimestamp(storageKey, durationMs) {
    const now = Date.now();
    const cached = persistedEndCache.get(storageKey);

    if (typeof cached === 'number' && cached > now) {
      return cached;
    }

    const saved = parseInt(readStorage(storageKey), 10);
    if (!Number.isNaN(saved) && saved > now) {
      persistedEndCache.set(storageKey, saved);
      return saved;
    }

    const end = now + durationMs;
    persistedEndCache.set(storageKey, end);
    writeStorage(storageKey, end);
    return end;
  }

  function getPersistedRemainingMs(bannerElement) {
    const storageKey = getPersistedStorageKey(bannerElement);
    const durationMs = getCountdownDurationMs(bannerElement);
    const end = resolvePersistedEndTimestamp(storageKey, durationMs);
    return Math.max(0, end - Date.now());
  }

  // The Campaigns app engine takes a timer over while its countdown runs for this page (the box says so);
  // otherwise the timer below keeps its own time.
  function engineOwns(element) {
    return Boolean(element && element.closest && element.closest('[data-eyr-countdown-state="running"]'));
  }

  function updateTimeDisplay(element, value, minDigits) {
    if (!element || engineOwns(element)) {
      return;
    }

    const digits = typeof minDigits === 'number' ? minDigits : 2;
    const formattedValue = String(value).padStart(digits, '0');

    if (element.textContent !== formattedValue) {
      element.textContent = formattedValue;
    }
  }

  function getNextMidnightUSTimezone() {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(now);
    const currentYear = parseInt(parts.find(function (part) { return part.type === 'year'; }).value, 10);
    const currentMonth = parseInt(parts.find(function (part) { return part.type === 'month'; }).value, 10);
    const currentDay = parseInt(parts.find(function (part) { return part.type === 'day'; }).value, 10);
    const currentHour = parseInt(parts.find(function (part) { return part.type === 'hour'; }).value, 10);
    const currentMinute = parseInt(parts.find(function (part) { return part.type === 'minute'; }).value, 10);
    const currentSecond = parseInt(parts.find(function (part) { return part.type === 'second'; }).value, 10);

    const currentTimeMs = (currentHour * 3600 + currentMinute * 60 + currentSecond) * 1000;
    const dayMs = 24 * 3600 * 1000;
    const targetDateStr = currentYear + '-' + String(currentMonth).padStart(2, '0') + '-' + String(currentDay).padStart(2, '0');
    let targetDate = parseDateInUSTimezone(targetDateStr, '00:00');

    if (targetDate.getTime() <= now.getTime()) {
      const tomorrow = new Date(targetDate.getTime() + dayMs);
      const tomorrowParts = formatter.formatToParts(tomorrow);
      const tomorrowYear = parseInt(tomorrowParts.find(function (part) { return part.type === 'year'; }).value, 10);
      const tomorrowMonth = parseInt(tomorrowParts.find(function (part) { return part.type === 'month'; }).value, 10);
      const tomorrowDay = parseInt(tomorrowParts.find(function (part) { return part.type === 'day'; }).value, 10);
      const tomorrowDateStr = tomorrowYear + '-' + String(tomorrowMonth).padStart(2, '0') + '-' + String(tomorrowDay).padStart(2, '0');
      targetDate = parseDateInUSTimezone(tomorrowDateStr, '00:00');
    }

    return targetDate;
  }

  function parseDateInUSTimezone(dateStr, timeStr) {
    const dateParts = dateStr.split('-').map(Number);
    const timeParts = timeStr.split(':').map(Number);
    const year = dateParts[0];
    const month = dateParts[1];
    const day = dateParts[2];
    const hours = timeParts[0];
    const minutes = timeParts[1];
    let targetUTC = Date.UTC(year, month - 1, day, hours + 5, minutes, 0);

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    for (let index = 0; index < 10; index += 1) {
      const checkDate = new Date(targetUTC);
      const parts = formatter.formatToParts(checkDate);
      const checkYear = parseInt(parts.find(function (part) { return part.type === 'year'; }).value, 10);
      const checkMonth = parseInt(parts.find(function (part) { return part.type === 'month'; }).value, 10);
      const checkDay = parseInt(parts.find(function (part) { return part.type === 'day'; }).value, 10);
      const checkHour = parseInt(parts.find(function (part) { return part.type === 'hour'; }).value, 10);
      const checkMinute = parseInt(parts.find(function (part) { return part.type === 'minute'; }).value, 10);

      if (checkYear === year && checkMonth === month && checkDay === day && checkHour === hours && checkMinute === minutes) {
        break;
      }

      const checkLocal = new Date(checkYear, checkMonth - 1, checkDay, checkHour, checkMinute, 0);
      const targetLocal = new Date(year, month - 1, day, hours, minutes, 0);
      const diffMs = targetLocal.getTime() - checkLocal.getTime();
      targetUTC += diffMs;

      if (Math.abs(diffMs) < 1000) {
        break;
      }
    }

    return new Date(targetUTC);
  }

  function createDurationMinutesTarget(minutes) {
    const durationMinutes = Math.max(1, parseInt(minutes, 10) || 10);
    return new Date(Date.now() + durationMinutes * 60 * 1000);
  }

  function renderRemainingMs(distance, showDays, daysElement, hoursElement, minutesElement, secondsElement) {
    const days = showDays ? Math.floor(distance / (1000 * 60 * 60 * 24)) : 0;
    const hours = showDays
      ? Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      : Math.floor(distance / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    if (daysElement && showDays) {
      updateTimeDisplay(daysElement, days, 2);
    }

    updateTimeDisplay(hoursElement, hours, 2);
    updateTimeDisplay(minutesElement, minutes, 2);
    updateTimeDisplay(secondsElement, seconds, 2);
  }

  function markExpired(bannerElement, daysElement, hoursElement, minutesElement, secondsElement, showTimerAfterExpired) {
    if (engineOwns(bannerElement)) {
      return;
    }

    if (daysElement) {
      daysElement.textContent = '00';
    }

    hoursElement.textContent = '00';
    minutesElement.textContent = '00';
    secondsElement.textContent = '00';
    bannerElement.classList.add('expired');

    const timerContainer = bannerElement.querySelector('[data-timer-container]');
    const textElement = bannerElement.querySelector('[data-text-element]');
    const expiredTextElement = bannerElement.querySelector('[data-expired-text]');
    const contentElement = bannerElement.querySelector('.countdown-banner__content');

    if (timerContainer) {
      timerContainer.style.display = showTimerAfterExpired ? 'flex' : 'none';
    }

    if (textElement) {
      textElement.style.display = 'none';
    }

    if (expiredTextElement) {
      expiredTextElement.style.display = 'block';
    }

    if (contentElement) {
      contentElement.style.justifyContent = 'center';
    }
  }

  function ensureHhmmssInterval() {
    if (hhmmssInterval) {
      return;
    }

    hhmmssInterval = window.setInterval(function () {
      const now = Date.now();

      for (let index = hhmmssTimers.length - 1; index >= 0; index -= 1) {
        const timer = hhmmssTimers[index];

        if (timer.active) {
          timer.tick(now);
        }
      }
    }, 1000);
  }

  function ensureMmssmsInterval() {
    if (mmssmsInterval) {
      return;
    }

    mmssmsInterval = window.setInterval(function () {
      const now = Date.now();

      for (let index = mmssmsTimers.length - 1; index >= 0; index -= 1) {
        const timer = mmssmsTimers[index];

        if (timer.active) {
          timer.tick(now);
        }
      }
    }, 100);
  }

  function registerHhmmssTimer(config) {
    const timer = {
      active: true,
      tick: function (now) {
        let distance = 0;

        if (config.mode === 'duration_persisted') {
          distance = getPersistedRemainingMs(config.bannerElement);
        } else if (config.mode === 'daily_24h') {
          distance = Math.max(0, config.targetDate.getTime() - now);

          if (distance <= 0) {
            config.targetDate = getNextMidnightUSTimezone();
            writeStorage('countdown_banner_daily_24h', String(config.targetDate.getTime()));
            distance = Math.max(0, config.targetDate.getTime() - now);
          }
        } else if (config.mode === 'duration_minutes') {
          distance = Math.max(0, config.targetDate.getTime() - now);

          if (distance <= 0) {
            config.targetDate = createDurationMinutesTarget(config.durationMinutes);
            distance = Math.max(0, config.targetDate.getTime() - now);
          }
        } else if (config.mode === 'end_ts' && config.phaseEnd && now < config.phaseEnd && config.targetDate.getTime() <= now) {
          // Campaigns app 24h countdown: at midnight the next day starts, until the phase ends.
          config.targetDate = new Date(Math.min(config.targetDate.getTime() + 86400000, config.phaseEnd));
          distance = Math.max(0, config.targetDate.getTime() - now);
        } else {
          distance = Math.max(0, config.targetDate.getTime() - now);

          if (distance <= 0) {
            timer.active = false;
            markExpired(
              config.bannerElement,
              config.daysElement,
              config.hoursElement,
              config.minutesElement,
              config.secondsElement,
              config.showTimerAfterExpired
            );
            return;
          }
        }

        renderRemainingMs(
          distance,
          config.showDays,
          config.daysElement,
          config.hoursElement,
          config.minutesElement,
          config.secondsElement
        );
      }
    };

    hhmmssTimers.push(timer);
    ensureHhmmssInterval();
    timer.tick(Date.now());
    return timer;
  }

  function registerMmssmsTimer(config) {
    const timer = {
      active: true,
      tick: function (now) {
        let distance = 0;

        if (config.mode === 'duration_persisted') {
          distance = getPersistedRemainingMs(config.bannerElement);
        } else {
          distance = Math.max(0, config.targetDate.getTime() - now);

          if (distance <= 0) {
            config.targetDate = createDurationMinutesTarget(config.durationMinutes);
            distance = Math.max(0, config.targetDate.getTime() - now);
          }
        }

        const totalMinutes = Math.floor(distance / (1000 * 60));
        const minutes = config.mode === 'duration_persisted' ? totalMinutes : totalMinutes % 100;
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        const milliseconds = Math.floor((distance % 1000) / 10);

        updateTimeDisplay(config.minutesElement, minutes, 2);
        updateTimeDisplay(config.secondsElement, seconds, 2);
        updateTimeDisplay(config.millisecondsElement, milliseconds, 2);
      }
    };

    mmssmsTimers.push(timer);
    ensureMmssmsInterval();
    timer.tick(Date.now());
    return timer;
  }

  function resolveDaily24hTargetDate() {
    const sessionKey = 'countdown_banner_daily_24h';
    const now = Date.now();
    const savedTimestamp = parseInt(readStorage(sessionKey), 10);

    if (!Number.isNaN(savedTimestamp) && savedTimestamp > now) {
      const savedDateObj = new Date(savedTimestamp);
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const savedParts = formatter.formatToParts(savedDateObj);
      const nowParts = formatter.formatToParts(new Date());

      if (
        savedParts.find(function (part) { return part.type === 'day'; }).value === nowParts.find(function (part) { return part.type === 'day'; }).value &&
        savedParts.find(function (part) { return part.type === 'month'; }).value === nowParts.find(function (part) { return part.type === 'month'; }).value &&
        savedParts.find(function (part) { return part.type === 'year'; }).value === nowParts.find(function (part) { return part.type === 'year'; }).value
      ) {
        return savedDateObj;
      }
    }

    const targetDate = getNextMidnightUSTimezone();
    writeStorage(sessionKey, String(targetDate.getTime()));
    return targetDate;
  }

  function initializeCountdown(bannerElement) {
    if (!bannerElement || bannerElement.dataset.agCountdownInit === 'true') {
      return;
    }

    const timerElement = bannerElement.querySelector('.countdown-banner__timer');
    if (!timerElement) {
      return;
    }

    bannerElement.dataset.agCountdownInit = 'true';

    const countdownMode = bannerElement.getAttribute('data-countdown-mode') || 'daily_24h';
    const countdownFormat = bannerElement.getAttribute('data-countdown-format') || 'hhmmss';
    const targetDateStr = bannerElement.getAttribute('data-target-date');
    const targetTimeStr = bannerElement.getAttribute('data-target-time') || '23:59';
    const showDays = bannerElement.getAttribute('data-show-days') === 'true';
    const showTimerAfterExpired = bannerElement.getAttribute('data-show-timer-after-expired') === 'true';

    if ((countdownMode === 'duration_minutes' || countdownMode === 'duration_persisted') && countdownFormat === 'mmssms') {
      const durationMinutes = parseInt(bannerElement.getAttribute('data-countdown-minutes'), 10) || 10;
      const minutesElement = timerElement.querySelector('.countdown-banner__minutes');
      const secondsElement = timerElement.querySelector('.countdown-banner__seconds');
      const millisecondsElement = timerElement.querySelector('.countdown-banner__milliseconds');

      if (!minutesElement || !secondsElement || !millisecondsElement) {
        return;
      }

      registerMmssmsTimer({
        mode: countdownMode,
        bannerElement: bannerElement,
        durationMinutes: durationMinutes,
        targetDate: countdownMode === 'duration_persisted'
          ? new Date(resolvePersistedEndTimestamp(getPersistedStorageKey(bannerElement), getCountdownDurationMs(bannerElement)))
          : createDurationMinutesTarget(durationMinutes),
        minutesElement: minutesElement,
        secondsElement: secondsElement,
        millisecondsElement: millisecondsElement
      });
      return;
    }

    const daysElement = showDays
      ? (timerElement.querySelector('.countdown-banner__days') || timerElement.querySelector('#countdown-days'))
      : null;
    const hoursElement = timerElement.querySelector('.countdown-banner__hours') || timerElement.querySelector('#countdown-hours');
    const minutesElement = timerElement.querySelector('.countdown-banner__minutes') || timerElement.querySelector('#countdown-minutes');
    const secondsElement = timerElement.querySelector('.countdown-banner__seconds') || timerElement.querySelector('#countdown-seconds');

    if (!hoursElement || !minutesElement || !secondsElement) {
      return;
    }

    let targetDate = new Date();

    if (countdownMode === 'daily_24h') {
      targetDate = resolveDaily24hTargetDate();
    } else if (countdownMode === 'duration_minutes') {
      targetDate = createDurationMinutesTarget(parseInt(bannerElement.getAttribute('data-countdown-minutes'), 10) || 10);
    } else if (countdownMode === 'duration_persisted') {
      targetDate = new Date(resolvePersistedEndTimestamp(getPersistedStorageKey(bannerElement), getCountdownDurationMs(bannerElement)));
    } else if (countdownMode === 'end_ts') {
      // Campaigns app: the end time printed by the server (epoch seconds).
      targetDate = new Date((parseInt(bannerElement.getAttribute('data-countdown-end'), 10) || 0) * 1000);
    } else if (countdownMode === 'specific_date' && targetDateStr) {
      try {
        targetDate = parseDateInUSTimezone(targetDateStr, targetTimeStr);

        if (Number.isNaN(targetDate.getTime())) {
          targetDate = getNextMidnightUSTimezone();
        }
      } catch (error) {
        targetDate = getNextMidnightUSTimezone();
      }
    } else {
      targetDate = getNextMidnightUSTimezone();
    }

    registerHhmmssTimer({
      mode: countdownMode,
      bannerElement: bannerElement,
      targetDate: targetDate,
      durationMinutes: parseInt(bannerElement.getAttribute('data-countdown-minutes'), 10) || 10,
      phaseEnd: (parseInt(bannerElement.getAttribute('data-countdown-phase-end'), 10) || 0) * 1000,
      showDays: showDays,
      showTimerAfterExpired: showTimerAfterExpired,
      daysElement: daysElement,
      hoursElement: hoursElement,
      minutesElement: minutesElement,
      secondsElement: secondsElement
    });
  }

  window.countdownBanner = {
    reset: function (storageKey) {
      removeStorage(storageKey || DEFAULT_PERSISTED_STORAGE_KEY);
      removeStorage('countdown_banner_daily_24h');
      window.location.reload();
    },
    getTimeRemaining: function (storageKey) {
      const key = storageKey || DEFAULT_PERSISTED_STORAGE_KEY;
      const end = parseInt(readStorage(key), 10);

      if (!Number.isNaN(end)) {
        return Math.max(0, end - Date.now());
      }

      return 0;
    }
  };

  function bootCountdowns() {
    const countdownBanners = document.querySelectorAll('.countdown-banner:not([data-ag-countdown-init="true"])');
    countdownBanners.forEach(function (banner) {
      initializeCountdown(banner);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootCountdowns);
  } else {
    bootCountdowns();
  }

  document.addEventListener('shopify:section:load', bootCountdowns);
})();
